import React, { useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Bold, Italic, Heading2, List, ListOrdered, Quote, Link as LinkIcon, Eye, Pencil } from "lucide-react";

/**
 * Editor Markdown minimale con toolbar + anteprima. Salva su una singola
 * stringa (`value`) usando la sintassi Markdown. Il render lato cliente
 * usa lo stesso `react-markdown` con classi tailwind-prose per il layout.
 *
 * Props:
 *   value: string
 *   onChange: (v: string) => void
 *   rows: number (righe textarea)
 *   placeholder: string
 *   testid: string (opzionale)
 */
export default function MarkdownEditor({ value, onChange, rows = 8, placeholder = "Scrivi qui…", testid }) {
  const textareaRef = useRef(null);
  const [preview, setPreview] = useState(false);

  const wrapSelection = (before, after = before, placeholder = "testo") => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const selected = value.slice(start, end) || placeholder;
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    onChange(next);
    // Ripristina focus e selezione sul testo appena wrappato
    setTimeout(() => {
      el.focus();
      const sStart = start + before.length;
      const sEnd = sStart + selected.length;
      el.setSelectionRange(sStart, sEnd);
    }, 0);
  };

  const insertLinePrefix = (prefix) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const before = value.slice(0, start);
    const selected = value.slice(start, end) || "voce";
    // Se c'è una selezione multiriga, applica il prefisso a ogni riga
    const linesPrefixed = selected.split("\n").map((l) => l ? `${prefix}${l}` : prefix.trim()).join("\n");
    const next = before + linesPrefixed + value.slice(end);
    onChange(next);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + prefix.length, start + linesPrefixed.length);
    }, 0);
  };

  const insertLink = () => {
    const el = textareaRef.current;
    if (!el) return;
    const url = window.prompt("URL del link (https://…)");
    if (!url) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const selected = value.slice(start, end) || "testo del link";
    const insert = `[${selected}](${url})`;
    onChange(value.slice(0, start) + insert + value.slice(end));
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + insert.length, start + insert.length);
    }, 0);
  };

  const btn = "p-1.5 rounded hover:bg-slate-200 text-slate-600 hover:text-blue-600 transition-colors";

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={testid}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-100 bg-slate-50 flex-wrap">
        <button type="button" onClick={() => wrapSelection("**")} className={btn} title="Grassetto (Ctrl+B)"><Bold size={14} /></button>
        <button type="button" onClick={() => wrapSelection("_")} className={btn} title="Corsivo (Ctrl+I)"><Italic size={14} /></button>
        <div className="w-px h-4 bg-slate-200 mx-1" />
        <button type="button" onClick={() => insertLinePrefix("## ")} className={btn} title="Titolo H2"><Heading2 size={14} /></button>
        <div className="w-px h-4 bg-slate-200 mx-1" />
        <button type="button" onClick={() => insertLinePrefix("- ")} className={btn} title="Elenco puntato"><List size={14} /></button>
        <button type="button" onClick={() => insertLinePrefix("1. ")} className={btn} title="Elenco numerato"><ListOrdered size={14} /></button>
        <button type="button" onClick={() => insertLinePrefix("> ")} className={btn} title="Citazione"><Quote size={14} /></button>
        <button type="button" onClick={insertLink} className={btn} title="Link"><LinkIcon size={14} /></button>
        <button
          type="button"
          onClick={() => setPreview((v) => !v)}
          className="ml-auto text-xs px-2 py-1 rounded border border-slate-200 hover:border-blue-400 hover:text-blue-600 text-slate-600 font-semibold bg-white flex items-center gap-1"
          data-testid={testid ? `${testid}-preview-toggle` : undefined}
        >
          {preview ? <><Pencil size={11} /> Modifica</> : <><Eye size={11} /> Anteprima</>}
        </button>
      </div>
      {/* Editor / Preview */}
      {preview ? (
        <div className="prose prose-slate prose-sm max-w-none p-3 min-h-[140px] bg-white">
          {value ? <ReactMarkdown>{value}</ReactMarkdown> : <p className="text-slate-400 italic">Nessun testo</p>}
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          className="w-full px-3 py-2 text-sm outline-none resize-none border-0 focus:ring-0"
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "b") { e.preventDefault(); wrapSelection("**"); }
            if ((e.metaKey || e.ctrlKey) && e.key === "i") { e.preventDefault(); wrapSelection("_"); }
          }}
        />
      )}
      <div className="px-3 py-1.5 text-[10px] text-slate-400 border-t border-slate-100 bg-slate-50">
        Puoi usare Markdown: **grassetto**, _corsivo_, ## titolo, - lista, &gt; citazione, [link](url)
      </div>
    </div>
  );
}
